import { useCallback, useMemo } from 'react';
import { NarrativeMiniValuatorForm } from './NarrativeMiniValuatorForm';
import type { NarrativeForm } from '@/pages/Narrative/utils/schema';
import type { NarrativeMiniValuatorScreen } from '../utils';
import { NARRATIVE_MINI_VALUATOR_COMPANY_QUERY_PARAM } from '../utils';
import type { NarrativeValuatorCompany } from '@/pages/Narrative/hooks/narrativeValuatorCompanyFragment';
import { useTracking } from 'react-tracking';
import { transformFormValuesToValuation } from '@/pages/Narrative/utils/transformation';
import { usePathname } from '@/hooks/location/usePathname';
import { useUpdatePublicNarrative } from '@/pages/Narrative/hooks/useUpdatePublicNarrative';
import { useNarrativeInvalidate } from '@/pages/Narrative/hooks/useNarrativeInvalidate';
import { usePastAnnualRevenueEarnings } from '@/pages/Narrative/hooks/usePastAnnualRevenueEarnings/usePastAnnualRevenueEarnings';
import type { FragmentOf } from 'gql.tada';
import { gql } from '@/utilities/gql';
import {
  CreateNarrativeFuturePEMultipleCalculator,
  ViewEditNarrativeFuturePEMultipleCalculator,
  useFuturePEMultipleCalculation,
  hasOnlyFairValue,
  FuturePEMultipleContext,
  calculateValuationInitialisation,
} from '../../Calculators';
import type { HeadingElements } from '@/utilities/heading';
import { VALUATION_FRAGMENT } from '@/pages/Narrative/hooks/valuationFragment';
import { useFlag } from '@simplywallst/unleash';
import { DRAFT_UPDATE_FRAGMENT } from '@/pages/Narrative/hooks/draftUpdateFragment';
import { LATEST_PUBLISHED_UPDATE_FRAGMENT } from '@/pages/Narrative/hooks/latestPublishedUpdateFragment';
import { useUpdateNarrativeUpdateWithSnackbar } from '@/pages/Narrative/hooks/useUpdateNarrativeUpdate/useUpdateNarrativeUpdate';
import {
  getInitialTitle,
  getInitialUserHoldsPosition,
  isLatestPublishedUpdateLocked,
} from '../../PublicNarrativeEdit/utils';
import { useStartNarrativeUpdateWithSnackbar } from '@/pages/Narrative/hooks/useStartNarrativeUpdate/useStartNarrativeUpdate';
import {
  NARRATIVE_EDIT_BUTTON_FRAGMENT,
  useNarrativeEditLink,
} from '../../NarrativeEditButton';
import { htmlToText } from '@/components/RichTextEditor';

export const NARRATIVE_MINI_VALUATOR_EDIT_NARRATIVE_FRAGMENT = gql(
  `
  fragment NarrativeMiniValuatorEditNarrativeFields on Narrative @_unmask {
    id
    title
    userHoldsPosition
    valuation {
      ...ValuationFields
    }
    draftUpdate {
      ...DraftUpdateFields
    }
    latestPublishedUpdate {
      ...LatestPublishedUpdateFields
    }
    ...NarrativeEditButtonFields
  },
`,
  [
    VALUATION_FRAGMENT,
    DRAFT_UPDATE_FRAGMENT,
    LATEST_PUBLISHED_UPDATE_FRAGMENT,
    NARRATIVE_EDIT_BUTTON_FRAGMENT,
  ]
);

type Props = {
  company: NarrativeValuatorCompany;
  setScreen: (screen: NarrativeMiniValuatorScreen) => void;
  narrative: FragmentOf<typeof NARRATIVE_MINI_VALUATOR_EDIT_NARRATIVE_FRAGMENT>;
  hideCompanySelect?: boolean;
  onSliderInteraction?: (isInteracting: boolean) => void;
  titleAs: HeadingElements;
};

export const NarrativeMiniValuatorFormEdit = ({
  company,
  setScreen,
  narrative,
  hideCompanySelect,
  onSliderInteraction,
  titleAs,
}: Props) => {
  const { trackEvent } = useTracking();
  const { invalidatePriority, invalidate } = useNarrativeInvalidate();

  const { mutate: updateNarrative, isPending } = useUpdatePublicNarrative({
    onSuccess: () => {
      invalidatePriority(['narrative-mini-valuator']);
      trackEvent({
        action: 'save',
      });
    },
  });

  const {
    enabled: updateNarrativeUpdateEnabled,
    ready: updateNarrativeUpdateReady,
  } = useFlag('narrative-content-from-updates');

  const isUpdateNarrativeUpdateEnabled =
    updateNarrativeUpdateEnabled && updateNarrativeUpdateReady;

  const isPublishedWithinGracePeriod =
    narrative?.latestPublishedUpdate?.publishedAt &&
    !isLatestPublishedUpdateLocked(narrative.latestPublishedUpdate);

  const isPublished = Boolean(
    narrative?.draftUpdate?.publishedAt ||
      narrative?.latestPublishedUpdate?.publishedAt
  );

  const initialTitle = isUpdateNarrativeUpdateEnabled
    ? getInitialTitle(
        narrative.draftUpdate ?? null,
        narrative.latestPublishedUpdate ?? null
      )
    : narrative.title;

  const initialUpdate = isPublishedWithinGracePeriod
    ? narrative.latestPublishedUpdate
    : narrative.draftUpdate;

  const initialContent = isUpdateNarrativeUpdateEnabled
    ? initialUpdate?.contentHtml ?? ''
    : '';
  const initialCoreContent = isUpdateNarrativeUpdateEnabled
    ? initialUpdate?.coreHtml ?? ''
    : '';

  const initialUserHoldsPosition = isUpdateNarrativeUpdateEnabled
    ? getInitialUserHoldsPosition(
        narrative.draftUpdate ?? null,
        narrative.latestPublishedUpdate ?? null
      )
    : narrative.userHoldsPosition;

  const isPublishedLocked =
    isPublished &&
    isLatestPublishedUpdateLocked(narrative.latestPublishedUpdate);

  const valuation = useMemo(() => {
    if (!isUpdateNarrativeUpdateEnabled) {
      return narrative.valuation;
    }

    return (
      narrative.draftUpdate?.valuation ??
      narrative.latestPublishedUpdate?.valuation
    );
  }, [
    isUpdateNarrativeUpdateEnabled,
    narrative.draftUpdate?.valuation,
    narrative.latestPublishedUpdate?.valuation,
    narrative.valuation,
  ]);

  const pastAnnualRevenueEarnings = usePastAnnualRevenueEarnings({
    company,
    fiscalData: isUpdateNarrativeUpdateEnabled
      ? valuation?.fiscalData ?? null
      : narrative.valuation.fiscalData,
  });

  const strategyContext = useMemo(
    () =>
      new FuturePEMultipleContext(
        !valuation || hasOnlyFairValue(valuation)
          ? new CreateNarrativeFuturePEMultipleCalculator(company)
          : new ViewEditNarrativeFuturePEMultipleCalculator(valuation)
      ),
    [company, valuation]
  );

  const {
    mutate: updateNarrativeUpdateAsync,
    isPending: isUpdatingNarrativeUpdate,
  } = useUpdateNarrativeUpdateWithSnackbar({
    includeSuccessSnackbar: true,
  });

  const { mutate: startNarrativeUpdateAsync } =
    useStartNarrativeUpdateWithSnackbar({
      includeSuccessSnackbar: true,
    });

  const handleSubmit = useCallback(
    async (values: NarrativeForm) => {
      const { id } = company;
      const { title } = values;

      const hasDraftUpdate = Boolean(narrative?.draftUpdate);
      const isLatestUpdateUnlocked =
        !narrative?.latestPublishedUpdate?.lockedAt;
      const canUpdateExistingUpdate = hasDraftUpdate || isLatestUpdateUnlocked;

      const shouldUpdateExistingUpdate =
        (isUpdateNarrativeUpdateEnabled && canUpdateExistingUpdate) ||
        (isPublishedWithinGracePeriod && isUpdateNarrativeUpdateEnabled);

      if (shouldUpdateExistingUpdate) {
        const narrativeUpdateId = isPublishedWithinGracePeriod
          ? narrative.latestPublishedUpdate?.id ?? ''
          : narrative.draftUpdate?.id ?? '';

        await updateNarrativeUpdateAsync({
          id: narrativeUpdateId,
          input: {
            title,
            contentHtml: values.content.html,
            coreHtml: values.coreContent.html,
            valuation: transformFormValuesToValuation({ values }),
            rebase: false,
            userHoldsPosition: values.userHoldsPosition ?? false,
            setAsPrimary: true,
          },
        });

        invalidate();

        trackEvent({
          action: 'save',
        });

        return;
      } else if (isUpdateNarrativeUpdateEnabled && isPublishedLocked) {
        await startNarrativeUpdateAsync({
          input: {
            narrativeId: narrative.id,
            title: values.title,
            coreHtml: values.content.html,
            valuation: transformFormValuesToValuation({ values }),
            userHoldsPosition: values.userHoldsPosition ?? false,
            setAsPrimary: true,
          },
        });

        invalidate();

        trackEvent({
          action: 'save',
        });

        return;
      }

      updateNarrative({
        input: {
          id: narrative.id,
          title,
          companyId: id,
          valuations: [
            transformFormValuesToValuation({
              values,
            }),
          ],
        },
      });
    },
    [
      company,
      narrative.draftUpdate,
      narrative.latestPublishedUpdate?.lockedAt,
      narrative.latestPublishedUpdate?.id,
      narrative.id,
      isUpdateNarrativeUpdateEnabled,
      isPublishedWithinGracePeriod,
      isPublishedLocked,
      updateNarrative,
      updateNarrativeUpdateAsync,
      invalidate,
      trackEvent,
      startNarrativeUpdateAsync,
    ]
  );

  const {
    calculateFairValue,
    calculateFairValueFromInitialisation,
    calculateRevenueGrowthRateFromFairValue,
  } = useFuturePEMultipleCalculation(strategyContext);

  const initialNarrativeForm: NarrativeForm = useMemo(() => {
    const calculatorOutputs = calculateValuationInitialisation({
      valuation: valuation!,
      calculateFairValueFromInitialisation,
      calculateRevenueGrowthRateFromFairValue,
    });

    return {
      ...calculatorOutputs,
      // Not modified by form, updatedNarrative ignores this field
      userHoldsPosition: initialUserHoldsPosition,
      title: initialTitle,
      content: {
        // Not modified by form, updatedNarrative ignores this field
        text: htmlToText(initialContent),
        html: initialContent,
      },
      coreContent: {
        text: htmlToText(initialCoreContent),
        html: initialCoreContent,
      },
    };
  }, [
    valuation,
    calculateFairValueFromInitialisation,
    calculateRevenueGrowthRateFromFairValue,
    initialUserHoldsPosition,
    initialTitle,
    initialContent,
    initialCoreContent,
  ]);

  const pathname = usePathname();
  const returnPath = `${pathname}?${NARRATIVE_MINI_VALUATOR_COMPANY_QUERY_PARAM}=${company.canonicalURL}`;
  const { editHref, createHref } = useNarrativeEditLink({
    narrative,
    companyId: company.id,
    returnPath,
  });

  return (
    <NarrativeMiniValuatorForm
      company={company}
      pastAnnualRevenueEarnings={pastAnnualRevenueEarnings}
      initialValues={initialNarrativeForm}
      setScreen={setScreen}
      onSubmit={handleSubmit}
      isSubmitting={isPending || isUpdatingNarrativeUpdate}
      saveLabel="Save"
      enableReinitialize
      fullScreenPath={editHref ?? createHref}
      calculateFairValue={calculateFairValue}
      calculateRevenueGrowthRateFromFairValue={
        calculateRevenueGrowthRateFromFairValue
      }
      hideCompanySelect={hideCompanySelect}
      onSliderInteraction={onSliderInteraction}
      titleAs={titleAs}
    />
  );
};
